import markRaw from "./mark.svg?raw";

import { DEFAULT_THEME_ID, type LogoTheme } from "./themes";

/** Re-rasterise the brand mark into the browser favicon + apple-touch icon for
 * the active theme. The mark SVG is themeable via `var(--logo-*)`; since a
 * favicon is rendered in isolation (no page CSS), we substitute the variables
 * with the theme's concrete colours, draw the SVG to a canvas, and add <link>s.
 * White stays `transparent` (the mark has no white).
 *
 * SEO constraint: this must never touch the static `<link rel="icon">` tags in
 * index.html. Those point at `/favicon.png` and `/apple-touch-icon-180x180.png`
 * — real, fetchable URLs — and they are the ONLY favicon a crawler can use. An
 * earlier version overwrote them in place with `canvas.toDataURL()` output; a
 * `data:` URI is not a URL Google can fetch or index, so the rendered page ended
 * up declaring no retrievable icon at all. Instead we append our own links,
 * tagged `data-gv-themed`, and remove them again on the default palette. */

const VAR_RE = /var\(--logo-(base|gold|accent|grain),\s*[^)]*\)/g;

/** Marks the <link>s this module owns, so it never disturbs the static ones. */
const OWNED = "data-gv-themed";

/** Match the assets referenced by index.html: our links come later in the head,
 * so a browser weighing equal-sized candidates settles on the themed one. */
const FAVICON_PX = 96;
const APPLE_PX = 180;

function themedMark(theme: LogoTheme): string {
  return markRaw.replace(VAR_RE, (_m, key: keyof LogoTheme) => theme[key]);
}

async function rasterize(svg: string, size: number): Promise<string> {
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  try {
    const img = new Image();
    img.decoding = "async";
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("favicon svg decode failed"));
      img.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    // Fit-contain the (near-square) card into the square icon, centred.
    const ar = (img.naturalWidth || 1) / (img.naturalHeight || 1);
    let w = size, h = size;
    if (ar > 1) h = size / ar;
    else w = size * ar;
    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Drop every themed link, leaving the crawlable static ones in sole effect. */
function clearThemed(): void {
  for (const el of document.head.querySelectorAll(`link[${OWNED}]`)) el.remove();
}

function setThemedLink(rel: string, href: string, size: number): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[${OWNED}][rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    el.setAttribute(OWNED, "");
    document.head.appendChild(el);
  }
  el.type = "image/png";
  el.sizes.value = `${size}x${size}`;
  el.href = href;
}

let token = 0;

/** Update the favicon + apple-touch icon to the given theme. Best-effort and
 * non-blocking — a failure here must never break the app. Guarded by a token so
 * a rapid theme switch only ever applies the latest. */
export async function applyFavicon(theme: LogoTheme): Promise<void> {
  const mine = ++token;

  // The default palette is precisely what the shipped /favicon.png and
  // /apple-touch-icon-180x180.png already contain, so re-rasterising it would
  // only shadow a real URL with a data: URI for no visual gain. Crawlers always
  // see this branch (the palette lives in localStorage), so it is the
  // SEO-critical one.
  if (theme.id === DEFAULT_THEME_ID) {
    clearThemed();
    return;
  }

  try {
    const svg = themedMark(theme);
    const [favicon, apple] = await Promise.all([
      rasterize(svg, FAVICON_PX),
      rasterize(svg, APPLE_PX),
    ]);
    if (mine !== token) return; // a newer theme won
    if (favicon) setThemedLink("icon", favicon, FAVICON_PX);
    if (apple) setThemedLink("apple-touch-icon", apple, APPLE_PX);
  } catch {
    /* keep the existing favicon */
  }
}
